import { useCallback, useEffect, useState } from "react";
import type { PackagesAutocompleteResult } from "@b2bportal/core-packages";
import { IconNameEnum } from "@b2bportal/core-themes";
import type { AutocompleteState } from "@b2bportal/core-ui";
import { Icon } from "@tenant/ui";

interface PackageLocationAutocompleteProps {
  onChange: (value: PackagesAutocompleteResult) => void;
  value?: PackagesAutocompleteResult;
}

export const usePackageLocationAutocomplete = ({
  onChange,
  value,
}: PackageLocationAutocompleteProps) => {
  const [currValue, setCurrValue] = useState<PackagesAutocompleteResult>(value);

  // Sync local state with prop changes
  useEffect(() => {
    setCurrValue(value);
  }, [value]);

  const handleOnChange = useCallback(
    (
      value: PackagesAutocompleteResult,
      _state: AutocompleteState<PackagesAutocompleteResult>
    ) => {
      setCurrValue(value);
      onChange(value);
    },
    [onChange]
  );

  const getOptionLabel = useCallback((option: PackagesAutocompleteResult) => {
    return option?.label ?? "";
  }, []);

  const getOptionIcon = useCallback((option: PackagesAutocompleteResult) => {
    // Eventually if we want different icons for different locations, we can update here,
    // for now we just use the default map pin icon since we can't map a label to a hotel/air/car.
    return <Icon iconName={IconNameEnum.mapPin} />;
  }, []);

  return {
    context: {
      value: currValue ?? null,
      placeholder: "",
    },
    handlers: {
      onChange: handleOnChange,
      getOptionLabel,
      getOptionIcon,
    },
  };
};
